import { useCallback, useEffect, useState } from "react";
import { isFullVideoData } from "../../utils";
import { useVideoDataContext } from "../../contexts/VideoDataContext";
import { useVideoElementRefContext } from "../../contexts/VideoElementRefContext";

export const PLAYER_STATE_INIT_VALUE = {
  isStarted: false,
  isMuted: false,
  isPlaying: false,
  volume: 1,
  showSubtitles: false,
  isInitialized: false
};

const getInitialState = (videoData, config) => {
  const userMutePreference =
    window.localStorage.getItem("userMutePreference") === "true";
  const userSubtitlesPreference =
    window.localStorage.getItem("userSubtitlesPreference") === "true";

  const { forceClosedCaptionsOn = false } =
    videoData.additional_properties || {};

  return {
    isMuted: config.isMutedOverride ?? userMutePreference,
    volume: userMutePreference ? 0 : 1,
    showSubtitles:
      config.showSubtitlesOverride ?? forceClosedCaptionsOn
        ? true
        : userSubtitlesPreference
  };
};

// TODO: move the related logic from useVideoControls to this hook
export function usePlayerState(config) {
  const videoElementRef = useVideoElementRefContext();
  const { videoData } = useVideoDataContext();

  const [playerState, setPlayerState] = useState(PLAYER_STATE_INIT_VALUE);

  const updatePlayerState = useCallback(
    newState => {
      setPlayerState(prevState => ({
        ...prevState,
        ...newState
      }));
    },
    [setPlayerState]
  );

  const playingHandler = useCallback(() => {
    if (!playerState.isStarted) {
      updatePlayerState({ isStarted: true });
    }
  }, [playerState, updatePlayerState]);

  useEffect(() => {
    const videoElement = videoElementRef?.current;
    if (!videoElement) return;

    videoElement.addEventListener("playing", playingHandler);

    return () => {
      videoElement.removeEventListener("playing", playingHandler);
    };
  }, [videoElementRef, playingHandler]);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !videoData ||
      !isFullVideoData(videoData) ||
      playerState.isInitialized
    )
      return;

    const initialState = getInitialState(videoData, config);

    updatePlayerState({ ...initialState, isInitialized: true });
  }, [
    config,
    config.isMutedOverride,
    config.showSubtitlesOverride,
    playerState.isInitialized,
    updatePlayerState,
    videoData,
    videoData._id
  ]);

  return {
    playerState,
    updatePlayerState
  };
}
